import { useState } from "react";
import { registerFarmer } from "../services/farmer/auth";

/**
 * Hook personnalisé pour gérer l'inscription des agriculteurs et des acheteurs
 * @returns {Object} - Fonctions et états pour gérer l'inscription
 */
const useFarmerRegistration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);

  /**
   * Valide les données du formulaire d'inscription
   * @param {Object} formData - Données du formulaire à valider
   * @returns {Object} - Résultat de la validation
   */
  const validateForm = (formData) => {
    // Vérifier les champs obligatoires selon le type d'utilisateur
    if (formData.userType === "buyer") {
      // Validation pour les acheteurs
      if (
        !formData.firstName ||
        !formData.lastName ||
        !formData.phone ||
        !formData.email ||
        !formData.password ||
        !formData.province
      ) {
        return {
          isValid: false,
          message: "Veuillez remplir tous les champs obligatoires",
        };
      }
      
      // Valider le format de l'email pour les acheteurs
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        return {
          isValid: false,
          message: "Veuillez entrer une adresse email valide",
        };
      }
    } else {
      // Validation pour les vendeurs (agriculteurs)
      if (
        !formData.fullName ||
        !formData.email ||
        !formData.password ||
        !formData.phone ||
        !formData.farmName ||
        !formData.farmLocation
      ) {
        return {
          isValid: false,
          message: "Veuillez remplir tous les champs obligatoires",
        };
      }

      // Valider le format de l'email pour les vendeurs
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        return {
          isValid: false,
          message: "Veuillez entrer une adresse email valide",
        };
      }
    }

    // Valider la longueur du mot de passe
    if (formData.password.length < 6) {
      return {
        isValid: false,
        message: "Le mot de passe doit contenir au moins 6 caractères",
      };
    }

    // Valider la confirmation du mot de passe si présente
    if (
      formData.confirmPassword &&
      formData.password !== formData.confirmPassword
    ) {
      return {
        isValid: false,
        message: "Les mots de passe ne correspondent pas",
      };
    }

    // Valider le numéro de téléphone (format simple)
    const phoneRegex = /^\+?[0-9]{8,15}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s/g, ""))) {
      return {
        isValid: false,
        message: "Veuillez entrer un numéro de téléphone valide",
      };
    }

    return { isValid: true };
  };

  /**
   * Effectue l'inscription d'un utilisateur (agriculteur ou acheteur)
   * @param {Object} formData - Données du formulaire d'inscription
   * @returns {Promise<Object>} - Résultat de l'inscription
   */
  const registerFarmerUser = async (formData) => {
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      // Valider le formulaire
      const validation = validateForm(formData);
      if (!validation.isValid) {
        setError(validation.message);
        setIsLoading(false);
        return { success: false, message: validation.message };
      }

      // Préparer les données selon le type d'utilisateur
      const userData = { ...formData };
      
      // Pour les acheteurs, définir automatiquement adminApproved à true
      if (formData.userType === "buyer") {
        userData.adminApproved = true;
        userData.status = "approved";
        
        // Construire le fullName à partir des prénom et nom
        userData.fullName = `${formData.firstName} ${formData.lastName}`;
      }

      // Effectuer l'inscription
      const result = await registerFarmer(userData);

      if (result.success) {
        setSuccess(true);
        setRegisteredUser(result.user);
      } else {
        setError(result.message);
      }

      setIsLoading(false);
      return result;
    } catch (err) {
      const errorMessage = 
        err.message || "Une erreur inattendue s'est produite lors de l'inscription";
      
      setError(errorMessage);
      setIsLoading(false);
      
      return { 
        success: false, 
        message: errorMessage 
      };
    }
  };

  /**
   * Réinitialise les états du hook
   */
  const resetState = () => {
    setError(null);
    setSuccess(false);
    setIsLoading(false);
    setRegisteredUser(null);
  };

  return {
    isLoading,
    error,
    success,
    registeredUser,
    registerFarmerUser,
    resetState,
    validateForm,
  };
};

export default useFarmerRegistration;